import {
  USER_ROLE,
  EVENTS,
  PEER_CONNECTION_STATES,
  CONNECTION_STATES
} from "./enums/LiveStreamingEnums";
import EventEmitter from "common/utils/EventEmitter.js";

import CustomErrorManager from "common/services/CustomErrorManager.js";
import CustomErrorCodeMessages from "common/enums/CustomErrorMessages.js";
import CustomErrorCodes from "common/enums/CustomErrorCodes.js";
import IceServersConfigService from "./IceServersConfigService";
import MediaTrackService from "./MediaTrackService";
import LoggerService from "./LoggerService";

/**
 * this class is responsible for handling lifecycle of a particular peer connection
 * @class
 */
export default class PeerConnection {
  /** @type {string}*/
  roomId;
  /** @type {string}*/
  peerId;
  userRole;
  /** @type {PeerConnection}*/
  connection;
  connectionState;

  /** @type {MediaStream} */
  stream = null;
  requestedStreams = {};
  config = {};
  iceServersConfig = null;
  connectionStartTime;

  /** @type {Array<RTCIceCandidate>} */
  pendingIceCandidates = [];

  constructor(config, requestedStreams, iceServersConfig) {
    this.roomId = config.roomId;
    this.peerId = config.peerId;
    this.userRole = config.userRole;
    this.requestedStreams = requestedStreams;
    this.config = config;
    this.iceServersConfig = iceServersConfig;
  }

  getIceServersConfig = async () => {
    if (!this.iceServersConfig) {
      this.iceServersConfig = await IceServersConfigService.getIceServersConfig();
    }

    return this.iceServersConfig;
  };

  createConnection = async (listeners = {}) => {
    try {
      this.connectionStartTime = Date.now();
      this.iceServersConfig = await this.getIceServersConfig();

      if (!this.iceServersConfig) {
        throw CustomErrorManager.NotFoundError(
          CustomErrorCodes.CONFIG_REQUEST_FAILED,
          CustomErrorCodeMessages.CONFIG_REQUEST_FAILED
        );
      }

      this.connection = new RTCPeerConnection(this.iceServersConfig);

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${this.peerId}] [PeerConnection Created] ${JSON.stringify(this.requestedStreams)}]`
      );

      this.listeners = listeners;
      this.connectionState = CONNECTION_STATES.Connecting;

      this.listenToConnectionStateChange();
      this.startSendingIceCandidates();

      if (this.userRole == USER_ROLE.BROADCASTER) {
        await this.addMediaStreams();
        await this.sendOffer();
      } else if (this.userRole == USER_ROLE.VIEWER) {
        this.listenToRemoteTrack();
      }
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `L4 - Failed to create peer connection for: roomId="${this.roomId}" peerId="${this.peerId}" requestedStreams="${JSON.stringify(this.requestedStreams)}"`
      );

      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_CREATE_PEER_CONNECTION,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_CREATE_PEER_CONNECTION,
        error
      );
    }
  };

  stopConnection = async () => {
    try {
      if (this.connection) {
        this.connectionState = CONNECTION_STATES.Closed;
        EventEmitter.emit("UPDATE_CONNECTION_STATE", {
          roomId: this.roomId,
          peerId: this.peerId,
          state: CONNECTION_STATES.Closed,
          requestedStreams: this.requestedStreams
        });
        this.stopSendingIceCandidates();
        await this.connection?.close();
        this.connection = null;
        this.stream = null;
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_CLOSE_PEER_CONNECTION,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_CLOSE_PEER_CONNECTION,
        error
      );
    }
  };

  /**
   * Asynchronously creates and sends an SDP (Session Description Protocol) offer.
   *
   * This method is responsible for initiating a peer-to-peer connection by creating
   * an offer, sending it to a listener, and handling pending ICE (Interactive Connectivity
   * Establishment) candidates.
   *
   * @async
   * @returns {Promise<void>} Resolves when the offer is successfully sent and pending ICE
   * candidates are added.
   * @throws {Error} If the `createOffer`, `sendOffer`, or `addPendingIceCandidates`
   * operations fail.
   *
   * @example
   * await sendOffer();
   */
  sendOffer = async () => {
    try {
      if (this.listeners) {
        const sdp = await this.createOffer();
        const status = await this.listeners.sendOffer(
          this.config,
          sdp,
          this.requestedStreams,
          this.iceServersConfig
        );

        if (status) {
          LoggerService.info(
            LoggerService.MODULE.STREAMING,
            `[peerId: ${this.peerId}] [SIGNALING] [OFFER SENT]`
          );
        } else {
          throw new Error(`Failed to send offer [SIGNALING] [peerId: ${this.peerId}]`);
        }
      }
    } catch (error) {
      this.stopConnection();
      throw error;
    }
  };

  /**
   * Asynchronously creates and sends an SDP (Session Description Protocol) answer.
   *
   * This method is used to respond to a received SDP offer and complete the signaling process.
   *
   * @async
   * @returns {Promise<void>} Resolves when the answer is successfully sent.
   * @throws {Error} If the `createAnswer` or `sendAnswer` operation fails.
   *
   * @example
   * await sendAnswer();
   */
  sendAnswer = async () => {
    try {
      if (this.listeners) {
        const sdp = await this.createAnswer();
        const status = await this.listeners.sendAnswer(this.config, sdp);

        if (status) {
          LoggerService.info(
            LoggerService.MODULE.STREAMING,
            `[peerId: ${this.peerId}] [SIGNALING] [ANSWER SENT]`
          );
        } else {
          throw new Error(`Failed to send answer [SIGNALING] [peerId: ${this.peerId}]`);
        }
      }
    } catch (error) {
      this.stopConnection();
      throw error;
    }
  };

  /**
   * Asynchronously creates an SDP (Session Description Protocol) offer.
   *
   * This method initializes the offer process for establishing a peer-to-peer connection.
   *
   * @async
   * @returns {Promise<RTCSessionDescription | null>} Resolves with the generated SDP offer,
   * or `null` if the connection is not available.
   * @throws {Error} If the `createOffer` or `setLocalDescription` operation fails.
   *
   * @example
   * // Usage example
   * const sdp = await createOffer();
   * if (sdp) {
   *   console.log("SDP Offer:", sdp);
   * }
   */
  createOffer = async () => {
    if (this.connection) {
      const sdp = await this.connection.createOffer();
      await this.setLocalDescription(sdp);
      return sdp;
    }
    return null;
  };

  /**
   * Asynchronously creates an SDP (Session Description Protocol) answer.
   * This method is used to respond to a received offer and complete the signaling process.
   *
   * @async
   * @returns {Promise<RTCSessionDescription | null>} Resolves with the generated SDP answer,
   * or `null` if the connection is not available.
   * @throws {Error} If the `createAnswer` or `setLocalDescription` operation fails.
   *
   * @example
   * // Usage example
   * const sdp = await createAnswer();
   * if (sdp) {
   *   console.log("SDP Answer:", sdp);
   * }
   */
  createAnswer = async () => {
    if (this.connection) {
      const sdp = await this.connection.createAnswer();
      await this.setLocalDescription(sdp);
      return sdp;
    }
    return null;
  };

  /**
   * Handles receiving an offer SDP from a remote peer.
   *
   * @async
   * @function onReceivingOffer
   * @param {RTCSessionDescriptionInit} sdp - The SDP offer received from the remote peer.
   * @returns {Promise<void>} Resolves after processing the offer and optionally sending an answer.
   * @example
   * await instance.onReceivingOffer(sdp);
   */
  onReceivingOffer = async (sdp) => {
    if (this.userRole == USER_ROLE.VIEWER) {
      await this.setRemoteDescription(sdp);
      await this.sendAnswer();
      this.addPendingIceCandidates();
    }
  };

  /**
   * Handles receiving an answer SDP from a remote peer.
   *
   * @async
   * @function onReceivingAnswer
   * @param {RTCSessionDescriptionInit} sdp - The SDP answer received from the remote peer.
   * @returns {Promise<void>} Resolves after processing the answer.
   * @example
   * await instance.onReceivingAnswer(sdp);
   */
  onReceivingAnswer = async (sdp) => {
    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[peerId: ${this.peerId}] [SIGNALING] [ANSWER RECEIVED]`
    );

    if (this.userRole == USER_ROLE.BROADCASTER) {
      await this.setRemoteDescription(sdp);
      this.addPendingIceCandidates();
    }
  };

  /**
   * Sets the local SDP description for the connection.
   *
   * @async
   * @function setLocalDescription
   * @param {RTCSessionDescriptionInit} sdp - The local SDP to set.
   * @returns {Promise<void>} Resolves after the local description is set.
   * @throws {Error} Throws an error if setting the local description fails.
   * @example
   * await instance.setLocalDescription(sdp);
   */
  setLocalDescription = async (sdp) => {
    try {
      if (this.connection) {
        await this.connection.setLocalDescription(sdp);

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [SIGNALING] [LocalDescription is set]`
        );
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SET_LOCAL_DESCRIPTION,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SET_LOCAL_DESCRIPTION,
        error
      );
    }
  };

  /**
   * Sets the remote SDP description for the connection.
   *
   * @async
   * @function setRemoteDescription
   * @param {RTCSessionDescriptionInit} sdp - The remote SDP to set.
   * @returns {Promise<void>} Resolves after the remote description is set.
   * @throws {Error} Throws an error if setting the remote description fails.
   * @example
   * await instance.setRemoteDescription(sdp);
   */
  setRemoteDescription = async (sdp) => {
    try {
      if (this.connection) {
        await this.connection.setRemoteDescription(sdp);

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [SIGNALING] [RemoteDescription is set]`
        );
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SET_REMOTE_DESCRIPTION,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SET_REMOTE_DESCRIPTION,
        error
      );
    }
  };

  /**
   * Sends an ICE candidate to the remote peer.
   *
   * @async
   * @function sendIceCandidate
   * @param {RTCIceCandidateInit} candidate - The ICE candidate to send.
   * @returns {Promise<void>} Resolves after the ICE candidate is sent.
   * @example
   * await instance.sendIceCandidate(candidate);
   */
  sendIceCandidate = async (candidate) => {
    if (this.listeners) {
      await this.listeners.sendIceCandidate(this.config, candidate);

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${this.peerId}] [SIGNALING] [ICE candidate SENT]`
      );
    }
  };

  /**
   * Handles receiving an ICE candidate from a remote peer.
   *
   * @async
   * @function onRecevingIceCandidate
   * @param {RTCIceCandidateInit} candidate - The ICE candidate received from the remote peer.
   * @returns {Promise<void>} Resolves after the ICE candidate is processed.
   * @example
   * await instance.onRecevingIceCandidate(candidate);
   */
  onRecevingIceCandidate = (candidate) => {
    this.addIceCandidate(candidate);
  };

  /**
   * Adds an ICE candidate to the connection.
   * If the remote description is not yet set, the candidate is added to the pending list.
   *
   * @async
   * @function addIceCandidate
   * @param {RTCIceCandidateInit} candidate - The ICE candidate to add.
   * @returns {Promise<void>} Resolves after the candidate is added or queued.
   * @example
   * instance.addIceCandidate(candidate);
   */
  addIceCandidate = async (candidate) => {
    if (this.connection?.remoteDescription) {
      try {
        this.connection.addIceCandidate(new RTCIceCandidate(candidate));

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [SIGNALING] [ICE candidate SET]`
        );
      } catch (error) {
        LoggerService.error(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [SIGNALING] [Failed to set ICE candidate]`,
          error
        );
      }
    } else {
      this.pendingIceCandidates.push(candidate);

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${this.peerId}] [SIGNALING] [ICE candidate BUFFERED]`
      );
    }
  };

  /**
   * Adds all pending ICE candidates to the connection.
   *
   * @async
   * @function addPendingIceCandidates
   * @example
   * instance.addPendingIceCandidates();
   */
  addPendingIceCandidates = () => {
    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[peerId: ${this.peerId}] [SIGNALING] [Buffered ICE candidates SET] [count: ${this.pendingIceCandidates.length}]`
    );

    for (let candidate of this.pendingIceCandidates) {
      if (this.connection?.remoteDescription) {
        try {
          this.connection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (error) {
          LoggerService.error(
            LoggerService.MODULE.STREAMING,
            `[peerId: ${this.peerId}] [SIGNALING] [Failed to add ICE candidate]`,
            error
          );
        }
      }
    }
    this.pendingIceCandidates = [];
  };

  listenToRemoteTrack = () => {
    this.connection.ontrack = (event) => {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${this.peerId}] [SIGNALING] [REMOTE MEDIA STREAM RECEIVED]`
      );

      if (this.stream == null) {
        this.stream = new MediaStream();
      }

      this.stream.addTrack(event.track);
      this.addTrackEndListener(event.track);
    };
  };

  /**
   * iterates over the tracks of given stream and adds track end event listener to each track
   * @param {MediaStreamTrack} track
   */
  addTrackEndListener = (track) => {
    // return if track is not valid
    if (!track) return;

    track.addEventListener("ended", () => {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] peerId: ${this.peerId} track ended of type ${track.kind}`
      );

      // stop the connection if track is closed
      this.stopConnection();
    });
  };

  listenToConnectionStateChange = () => {
    this.connection.onconnectionstatechange = () => {
      switch (this.connection.connectionState) {
        case PEER_CONNECTION_STATES.New:
          this.connectionState = CONNECTION_STATES.Connecting;
          break;

        case PEER_CONNECTION_STATES.Connecting:
          this.connectionState = CONNECTION_STATES.Connecting;
          EventEmitter.emit("UPDATE_CONNECTION_STATE", {
            roomId: this.roomId,
            peerId: this.peerId,
            state: CONNECTION_STATES.Connecting,
            requestedStreams: this.requestedStreams
          });
          break;

        case PEER_CONNECTION_STATES.Connected:
          this.connectionState = CONNECTION_STATES.Connected;
          EventEmitter.emit("UPDATE_CONNECTION_STATE", {
            roomId: this.roomId,
            peerId: this.peerId,
            state: CONNECTION_STATES.Connected,
            requestedStreams: this.requestedStreams,
            connectionDuration: Date.now() - this.connectionStartTime
          });
          break;

        case PEER_CONNECTION_STATES.Disconnected:
          this.stopConnection();
          break;

        case PEER_CONNECTION_STATES.Failed:
          this.stopConnection();

          break;

        case PEER_CONNECTION_STATES.Closed:
          this.stopConnection();
          break;

        default:
          break;
      }
    };
  };

  startSendingIceCandidates = () => {
    if (this.connection) {
      this.connection.addEventListener(EVENTS.IceCandidate, this.iceCandidatesListener);

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${this.peerId}] [listening to ice candidates]`
      );
    }
  };

  stopSendingIceCandidates = () => {
    if (this.connection) {
      this.connection.removeEventListener(EVENTS.IceCandidate, this.iceCandidatesListener);
    }
  };

  iceCandidatesListener = (event) => {
    if (event.candidate) {
      this.sendIceCandidate(event.candidate);
    }
  };

  addMediaStreams = async () => {
    if (this.requestedStreams.webcam) {
      const webcamStream = MediaTrackService.getLocalWebcamStream();

      if (webcamStream) {
        this.addTrack(webcamStream);

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [WEBCAM STREAM ADDED]`
        );
      }
    }

    if (this.requestedStreams.screen) {
      const screenshareStream = MediaTrackService.getLocalScreenshareStream();
      if (screenshareStream) {
        this.addTrack(screenshareStream);

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[peerId: ${this.peerId}] [SCREENSHARE STREAM ADDED]`
        );
      }
    }
  };

  addTrack = (stream) => {
    try {
      if (stream) {
        stream.getTracks().forEach((track) => {
          this.connection.addTrack(track, stream);

          LoggerService.info(
            LoggerService.MODULE.STREAMING,
            `[peerId: ${this.peerId}] [${track.kind} TRACK ADDED]`
          );
        });
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_ADD_MEDIA_TRACK,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_ADD_MEDIA_TRACK
      );
    }
  };

  removeTrack = () => {
    try {
      if (this.connection) {
        let senders = this.connection.getSenders();
        if (senders?.length > 0) {
          this.connection.removeTrack(senders[0]);
        }
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_TRACK,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_TRACK
      );
    }
  };

  getStream = () => {
    return this.stream;
  };

  getConnectionState = () => {
    return this.connectionState;
  };

  getSignalingState = () => {
    return this.connection?.signalingState;
  };

  getIceConnectionState = () => {
    return this.connection?.iceConnectionState;
  };
}
